/**
 * VideoCallSetupModal: configuração da chamada de vídeo (VCALL-06..13, 59, 72; PRD US-4).
 *
 * Simples como o Meet: o que importa fica à vista e o resto fica recolhido. À vista: a forma de envio (uma lista só,
 * com o padrão já marcado), "Transcrever a chamada" e o botão principal. Em "Mais opções" (recolhido, com o resumo do
 * que está valendo ao lado): a entrada do convidado e, no link, o título e a validade.
 *
 * Três formas: "Pelo app", "Por canal" (lista de canais com nome e tipo, que só se abre quando ela é a escolhida, ou
 * quando nenhum canal está disponível, para mostrar o motivo de cada um) e "Gerar link para copiar".
 * Cada forma indisponível aparece desabilitada com o motivo. A opção padrão vem de `defaultOption`.
 * O modo de entrada (aberto / com aprovação) some com "Pelo app". "Gerar link" pede título
 * (opcional) e validade dentro dos limites do SSM. Reaberto após o app não responder: sem a opção
 * app, com o aviso e o modo de entrada mantido. "Transcrever a chamada" é escolha do atendente a cada
 * chamada, desligada por padrão: aparece travada, com o motivo, quando o plano da conta não tem
 * `calling.video_transcription` (`options.transcription.available`).
 *
 * Sem contato (`setup.contactId` nulo: "Criar link para depois" da área Chamadas de vídeo) a sala é só por link: a lista de
 * formas de envio some, a entrada começa "Com aprovação" (não se sabe quem vai abrir o link) e "Mais opções" já vem aberto,
 * porque o título e a validade são o motivo de abrir este modal.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, Link2, MessageSquare, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useVideoCall } from '@/contexts/VideoCallContext';
import { cn } from '@/lib/utils';
import {
    BLOCKED_REASON_TEXT, NO_CHANNEL_AVAILABLE_TEXT, OPTION_REASON_TEXT, START_ERROR_TEXT, channelTypeLabel, formatMinutes,
} from '@/components/videoCall/videoCallCopy';
import { useStartVideoCall, useVideoCallOptions } from '@/features/otto/api/useVideoCallApi';

const TTL_UNITS = [
    { value: 'minutes', label: 'minutos', factor: 1 },
    { value: 'hours', label: 'horas', factor: 60 },
    { value: 'days', label: 'dias', factor: 1440 },
];

/** Maior unidade que representa o valor sem fração (1440 → 1 dia). */
function splitTtl(minutes) {
    if (minutes % 1440 === 0) return { amount: String(minutes / 1440), unit: 'days' };
    if (minutes % 60 === 0) return { amount: String(minutes / 60), unit: 'hours' };
    return { amount: String(minutes), unit: 'minutes' };
}

const toMinutes = ({ amount, unit }) => Number(amount) * TTL_UNITS.find((u) => u.value === unit).factor;

/**
 * Seleção inicial: `defaultOption`. Com o app excluído, segue a cadeia de VCALL-11 sem ele: canal do
 * ticket (`ticketChannelId`) se disponível, senão link.
 */
function initialSelection(options, excludeApp) {
    const def = options?.defaultOption;
    if (!def) return 'LINK';
    if (def.method === 'APP') {
        if (!excludeApp) return 'APP';
        const ticketChannel = options.channels?.find((c) => c.id === options.ticketChannelId && c.available);
        return ticketChannel ? `CHANNEL:${ticketChannel.id}` : 'LINK';
    }
    if (def.method === 'CHANNEL') return `CHANNEL:${def.channelId}`;
    return 'LINK';
}

/** Uma forma de envio: ícone, nome, uma linha de explicação e, se indisponível, o motivo. As três dividem um cartão só. */
function ChoiceRow({ id, name, icon: Icon, checked, disabled, onChange, label, detail, reason, children }) {
    return (
        <div className={cn(checked && 'bg-primary/5', disabled && 'opacity-60')}>
            <label
                htmlFor={id}
                className={cn('flex items-start gap-3 px-4 py-3', disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-primary/5')}
            >
                <input
                    id={id}
                    type="radio"
                    name={name}
                    className="mt-1 accent-primary"
                    checked={checked}
                    disabled={disabled}
                    onChange={onChange}
                    aria-describedby={reason ? `${id}-reason` : undefined}
                />
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-foreground-muted" aria-hidden="true" />
                <span className="flex-1">
                    <span className="block text-sm font-medium text-foreground">{label}</span>
                    {detail && <span className="block text-xs text-foreground-muted">{detail}</span>}
                    {reason && <span id={`${id}-reason`} className="block text-xs text-amber-700 dark:text-amber-400">{reason}</span>}
                </span>
            </label>
            {children}
        </div>
    );
}

/** Um canal dentro de "Por canal": só uma linha, sem caixa própria. */
function ChannelRow({ id, name, checked, disabled, onChange, label, detail, reason }) {
    return (
        <label htmlFor={id} className={cn('flex items-start gap-2.5 py-1', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
            <input
                id={id}
                type="radio"
                name={name}
                className="mt-1 accent-primary"
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                aria-describedby={reason ? `${id}-reason` : undefined}
            />
            <span className="flex-1">
                <span className="block text-sm text-foreground">{label}</span>
                {detail && <span className="block text-xs text-foreground-muted">{detail}</span>}
                {reason && <span id={`${id}-reason`} className="block text-xs text-amber-700 dark:text-amber-400">{reason}</span>}
            </span>
        </label>
    );
}

/** Uma das duas entradas do convidado (aberto / com aprovação), lado a lado. */
function EntryChoice({ id, checked, onChange, label, detail }) {
    return (
        <label
            htmlFor={id}
            className={cn('flex cursor-pointer items-start gap-2.5 rounded-lg border p-3', checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-surface-hover/50')}
        >
            <input id={id} type="radio" name="vc-entry" className="mt-1 accent-primary" checked={checked} onChange={onChange} />
            <span>
                <span className="block text-sm font-medium text-foreground">{label}</span>
                <span className="block text-xs text-foreground-muted">{detail}</span>
            </span>
        </label>
    );
}

/**
 * @param {{
 *   setup: { contactId: string, contactName?: string|null, ticketId?: string|null,
 *            entryMode?: 'OPEN'|'APPROVAL'|null, excludeApp?: boolean, notice?: string|null },
 *   onClose: () => void,
 *   onStarted: (created: object, info: object) => void,
 * }} props
 */
export function VideoCallSetupModal({ setup, onClose, onStarted }) {
    const { contactId = null, contactName = null, ticketId = null, excludeApp = false, notice = null } = setup;
    const contactless = !contactId;
    const optionsQuery = useVideoCallOptions({ contactId, ticketId, contactless });
    const options = optionsQuery.data;
    const startMutation = useStartVideoCall();

    const [selection, setSelection] = useState(null);
    const [entryMode, setEntryMode] = useState(setup.entryMode || (contactless ? 'APPROVAL' : 'OPEN'));
    const [title, setTitle] = useState('');
    const [ttl, setTtl] = useState(null);
    const [inlineError, setInlineError] = useState(null);
    const [transcribe, setTranscribe] = useState(false);
    const [moreOpen, setMoreOpen] = useState(contactless);

    const bounds = options?.link?.ttlMinutes ?? null;

    useEffect(() => {
        if (!options) return;
        setSelection((prev) => prev ?? initialSelection(options, excludeApp));
        setTtl((prev) => prev ?? splitTtl(options.link.ttlMinutes.default));
    }, [options, excludeApp]);

    const availableChannels = useMemo(() => (options?.channels ?? []).filter((c) => c.available), [options]);
    const noChannel = Boolean(options) && availableChannels.length === 0;

    const method = selection?.startsWith('CHANNEL:') ? 'CHANNEL' : selection;
    const channelId = method === 'CHANNEL' ? selection.slice('CHANNEL:'.length) : null;
    const selectedChannel = channelId ? options?.channels.find((c) => c.id === channelId) : null;

    const ttlMinutes = ttl ? toMinutes(ttl) : null;
    const ttlInvalid = method === 'LINK' && bounds && ttlMinutes !== null
        && (!Number.isFinite(ttlMinutes) || ttlMinutes < bounds.min || ttlMinutes > bounds.max);
    const ttlError = ttlInvalid
        ? `A validade deve ficar entre ${formatMinutes(bounds.min)} e ${formatMinutes(bounds.max)}`
        : null;

    const transcriptionAvailable = options?.transcription?.available === true;

    const blockedText = options?.blockedReason ? BLOCKED_REASON_TEXT[options.blockedReason] : null;
    const canSubmit = Boolean(options) && Boolean(method) && !ttlInvalid && !blockedText && !startMutation.isPending;

    // "Mais opções" só existe onde há o que ajustar: a entrada do convidado e o link (pelo app não há nada disso).
    const hasMoreOptions = Boolean(options) && Boolean(method) && method !== 'APP';
    const moreSummary = [
        entryMode === 'APPROVAL' ? 'Com aprovação' : 'Entrada aberta',
        method === 'LINK' && ttlMinutes !== null && !ttlInvalid ? `link válido por ${formatMinutes(ttlMinutes)}` : null,
    ].filter(Boolean).join(' · ');

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!canSubmit) return;
        setInlineError(null);
        const body = {
            contactId,
            ticketId,
            deliveryMethod: method,
            entryMode,
            ...(method === 'CHANNEL' ? { channelId } : {}),
            ...(method === 'LINK' ? { title: title.trim() || null, ttlMinutes } : {}),
            ...(transcribe && transcriptionAvailable ? { transcribe: true } : {}),
        };
        try {
            const created = await startMutation.mutateAsync(body);
            onStarted(created, {
                contactId,
                contactName,
                ticketId,
                channelName: selectedChannel?.name ?? null,
                channelType: selectedChannel?.type ?? null,
            });
        } catch (err) {
            if (err?.code === 'DELIVERY_OPTION_UNAVAILABLE' && BLOCKED_REASON_TEXT[err.reason]) {
                // Bloqueio surgido entre abrir o modal e iniciar (ex.: outro atendente ligou para o
                // contato, VCALL-77): as opções recarregadas mostram o motivo no aviso do topo.
                setSelection(null);
                optionsQuery.refetch();
                return;
            }
            if (err?.code === 'DELIVERY_OPTION_UNAVAILABLE' || err?.code === 'DELIVERY_FAILED') {
                setInlineError(START_ERROR_TEXT[err.code]);
                setSelection(null);
                optionsQuery.refetch();
                return;
            }
            if (err?.code === 'TRANSCRIPTION_NOT_AVAILABLE') {
                // O plano mudou entre abrir o modal e iniciar: recarrega as opções (a opção passa a vir travada).
                setTranscribe(false);
                setInlineError(START_ERROR_TEXT.TRANSCRIPTION_NOT_AVAILABLE);
                optionsQuery.refetch();
                return;
            }
            if (err?.code === 'INVALID_LINK_TTL') {
                setInlineError(START_ERROR_TEXT.INVALID_LINK_TTL);
                setMoreOpen(true); // a validade está em "Mais opções": o erro aponta para um campo que precisa estar à vista
                return;
            }
            toast.error(START_ERROR_TEXT[err?.code] ?? 'Não foi possível iniciar a chamada de vídeo');
        }
    };

    const appReason = options && !options.app.available ? OPTION_REASON_TEXT[options.app.reason] : null;

    return (
        <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
                <form onSubmit={handleSubmit} className="space-y-5">
                    <DialogHeader>
                        <DialogTitle>Chamada de vídeo</DialogTitle>
                        <DialogDescription>
                            {contactless
                                ? 'A sala fica aberta até o link expirar. Quem tem o link entra nela.'
                                : contactName ? `Como a chamada chega a ${contactName}?` : 'Como a chamada chega ao contato?'}
                        </DialogDescription>
                    </DialogHeader>

                    {notice && (
                        <div role="alert" className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
                            {notice}
                        </div>
                    )}
                    {blockedText && (
                        <div role="alert" className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                            {blockedText}
                        </div>
                    )}
                    {inlineError && (
                        <div role="alert" className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                            {inlineError}
                        </div>
                    )}

                    {optionsQuery.isLoading && <p className="text-sm text-foreground-muted">Carregando opções…</p>}
                    {optionsQuery.isError && <p role="alert" className="text-sm text-red-600">Não foi possível carregar as opções de envio.</p>}

                    {options && !contactless && (
                        <fieldset>
                            <legend className="sr-only">Forma de envio</legend>
                            <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                                {!excludeApp && (
                                    <ChoiceRow
                                        id="vc-method-app"
                                        name="vc-method"
                                        icon={Smartphone}
                                        label="Pelo app"
                                        detail="O contato recebe a chamada no app Touch Connect"
                                        checked={selection === 'APP'}
                                        disabled={!options.app.available}
                                        reason={appReason}
                                        onChange={() => setSelection('APP')}
                                    />
                                )}

                                <ChoiceRow
                                    id="vc-method-channel"
                                    name="vc-method-group"
                                    icon={MessageSquare}
                                    label="Por canal"
                                    detail="A plataforma envia o link por um canal de mensagem"
                                    checked={method === 'CHANNEL'}
                                    disabled={noChannel}
                                    reason={noChannel ? NO_CHANNEL_AVAILABLE_TEXT : null}
                                    onChange={() => availableChannels[0] && setSelection(`CHANNEL:${availableChannels[0].id}`)}
                                >
                                    {options.channels.length > 0 && (method === 'CHANNEL' || noChannel) && (
                                        <div role="radiogroup" aria-label="Canais" className="ml-[3.25rem] space-y-0.5 pb-3 pr-4">
                                            {options.channels.map((channel) => (
                                                <ChannelRow
                                                    key={channel.id}
                                                    id={`vc-channel-${channel.id}`}
                                                    name="vc-method"
                                                    label={`${channel.name} · ${channelTypeLabel(channel.type)}`}
                                                    detail={channel.available && channel.willUse === 'TEMPLATE'
                                                        ? 'Fora da janela de 24h: envio por modelo de mensagem'
                                                        : null}
                                                    checked={selection === `CHANNEL:${channel.id}`}
                                                    disabled={!channel.available}
                                                    reason={channel.available ? null : OPTION_REASON_TEXT[channel.reason] ?? null}
                                                    onChange={() => setSelection(`CHANNEL:${channel.id}`)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </ChoiceRow>

                                <ChoiceRow
                                    id="vc-method-link"
                                    name="vc-method"
                                    icon={Link2}
                                    label="Gerar link para copiar"
                                    detail="Você envia o link pelo meio que quiser"
                                    checked={selection === 'LINK'}
                                    disabled={false}
                                    onChange={() => setSelection('LINK')}
                                />
                            </div>
                        </fieldset>
                    )}

                    {options && method && (
                        <TranscriptionRow
                            available={transcriptionAvailable}
                            checked={transcribe}
                            onChange={setTranscribe}
                            appOnly={method === 'APP'}
                        />
                    )}

                    {hasMoreOptions && (
                        <section>
                            <button
                                type="button"
                                aria-expanded={moreOpen}
                                aria-controls="vc-more-options"
                                onClick={() => setMoreOpen((open) => !open)}
                                className="flex w-full items-center gap-2 rounded-md py-1 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                                <ChevronDown className={cn('h-4 w-4 shrink-0 text-foreground-muted transition-transform', moreOpen && 'rotate-180')} aria-hidden="true" />
                                <span className="font-medium text-foreground">Mais opções</span>
                                {!moreOpen && <span className="min-w-0 truncate text-xs text-foreground-muted">{moreSummary}</span>}
                            </button>

                            <div id="vc-more-options" hidden={!moreOpen} className="space-y-4 pt-3">
                                <fieldset>
                                    <legend className="mb-1.5 text-xs font-medium text-foreground">Entrada do convidado</legend>
                                    <div className="grid gap-2 sm:grid-cols-2">
                                        <EntryChoice
                                            id="vc-entry-open"
                                            label="Aberto"
                                            detail="Quem tem o link entra direto"
                                            checked={entryMode === 'OPEN'}
                                            onChange={() => setEntryMode('OPEN')}
                                        />
                                        <EntryChoice
                                            id="vc-entry-approval"
                                            label="Com aprovação"
                                            detail="Você aprova cada convidado na sala de espera"
                                            checked={entryMode === 'APPROVAL'}
                                            onChange={() => setEntryMode('APPROVAL')}
                                        />
                                    </div>
                                </fieldset>

                                {method === 'LINK' && ttl && (
                                    <div className="space-y-3">
                                        <div className="space-y-1">
                                            <label htmlFor="vc-title" className="text-xs font-medium">Título da reunião (opcional)</label>
                                            <Input
                                                id="vc-title"
                                                value={title}
                                                maxLength={120}
                                                placeholder={contactName ? `Chamada com ${contactName}` : 'Chamada de vídeo'}
                                                onChange={(e) => setTitle(e.target.value)}
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label htmlFor="vc-ttl" className="text-xs font-medium">Validade do link</label>
                                            <div className="flex gap-2">
                                                <Input
                                                    id="vc-ttl"
                                                    type="number"
                                                    min="1"
                                                    className="w-24"
                                                    value={ttl.amount}
                                                    aria-invalid={ttlInvalid || undefined}
                                                    aria-describedby="vc-ttl-help"
                                                    onChange={(e) => setTtl((prev) => ({ ...prev, amount: e.target.value }))}
                                                />
                                                <select
                                                    aria-label="Unidade da validade"
                                                    className="rounded-md border border-border bg-background px-2 text-sm"
                                                    value={ttl.unit}
                                                    onChange={(e) => setTtl((prev) => ({ ...prev, unit: e.target.value }))}
                                                >
                                                    {TTL_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                                                </select>
                                            </div>
                                            <p id="vc-ttl-help" className={cn('text-xs', ttlError ? 'text-red-600' : 'text-foreground-muted')}>
                                                {ttlError ?? `Entre ${formatMinutes(bounds.min)} e ${formatMinutes(bounds.max)}`}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
                        <Button type="submit" disabled={!canSubmit}>
                            {startMutation.isPending ? 'Iniciando…' : method === 'LINK' ? 'Gerar link' : 'Iniciar chamada'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

/**
 * "Transcrever a chamada": registra o que cada pessoa diz, e todos veem o ícone de gravação. Desligada por padrão. Sem a
 * chave no plano da conta a opção aparece travada, com o motivo. `appOnly`: pelo app só a fala do atendente entra na
 * transcrição (o contato pelo app ainda não é transcrito).
 */
function TranscriptionRow({ available, checked, onChange, appOnly }) {
    const detail = !available
        ? 'Não está incluída no plano desta conta.'
        : appOnly
            ? 'Pelo app, só a sua fala é transcrita: o contato pelo app ainda não entra na transcrição.'
            : 'Registra o que cada pessoa diz. Todos veem o ícone de gravação.';
    return (
        <div className={cn('flex items-center justify-between gap-4', !available && 'opacity-70')}>
            <div className="space-y-0.5">
                <label htmlFor="vc-transcribe" className="text-sm font-medium">Transcrever a chamada</label>
                <p id="vc-transcribe-help" className="text-xs text-foreground-muted">{detail}</p>
            </div>
            <Switch
                id="vc-transcribe"
                checked={available && checked}
                disabled={!available}
                onCheckedChange={onChange}
                aria-describedby="vc-transcribe-help"
            />
        </div>
    );
}

/** Liga o pedido de configuração do contexto ao modal (montado uma vez no layout). */
export function VideoCallSetupHost() {
    const { setup, closeSetup, beginCall } = useVideoCall();
    if (!setup) return null;
    return (
        <VideoCallSetupModal
            key={`${setup.contactId}:${setup.excludeApp}:${setup.notice ?? ''}`}
            setup={setup}
            onClose={closeSetup}
            onStarted={beginCall}
        />
    );
}

export default VideoCallSetupModal;
